import { Fragment, useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" }) : "");
const DIR = { loading: "LOADED", unloading: "UNLOADED", no_change: "NO CHANGE", review: "REVIEW" };

function csv(rep) {
  const lines = [["Customer", "Date", "Arrived", "Left", "Loading spot", "Plate", "Direction", "Slabs", "Before", "After", "Status"]];
  rep.customers.forEach((g) => g.visits.forEach((v) => lines.push([g.customer, v.day, tm(v.arrive_ts), tm(v.depart_ts), v.dock, v.plate,
    DIR[v.direction] || v.direction, v.slabs, v.arrive_slabs ?? "", v.depart_slabs ?? "", v.needs_review ? "NEEDS REVIEW" : v.reviewed ? "STAFF CONFIRMED" : "AI"])));
  const body = lines.map((l) => l.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
  a.download = `av-stone-slabs-by-customer_${rep.start}_${rep.end}.csv`;
  a.click();
}

export function CustomerLoadReport() {
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [rep, setRep] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/avstone/dock/report", { params: { start, end } }).then((r) => setRep(r.data)).catch(() => {}), [start, end]);
  useEffect(() => { load(); }, [load]);
  const t = rep?.totals || {};
  return (
    <div className="border border-white/10 p-4 space-y-3" data-testid="customer-load-report">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display font-black text-base">Slabs by customer</h2>
          <p className="text-xs text-[#71717A]">Confirmed counts only — visits that need review are listed but not added until staff correct them. Export to compare with invoices.</p>
        </div>
        <div className="flex gap-2 items-center text-xs">
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-black border border-white/20 px-2 py-1" data-testid="report-start" />
          <span>to</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-black border border-white/20 px-2 py-1" data-testid="report-end" />
          <button onClick={() => rep && csv(rep)} disabled={!rep?.customers?.length} className="px-3 py-1 bg-white text-black font-bold disabled:opacity-40" data-testid="report-export-btn">Export CSV</button>
        </div>
      </div>
      <div className="text-xs font-mono-data text-[#A1A1AA]" data-testid="report-totals">
        {t.slabs_loaded ?? 0} slabs loaded on {t.trucks_loaded ?? 0} truck(s) · {t.slabs_unloaded ?? 0} unloaded from {t.trucks_unloaded ?? 0} · {t.review ?? 0} need review
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] font-mono-data uppercase text-[#71717A] text-left">
          <th className="py-1">Customer</th><th>Plates</th><th className="text-right">Trucks loaded</th><th className="text-right">Slabs loaded</th><th className="text-right">Slabs unloaded</th><th className="text-right">Review</th>
        </tr></thead>
        <tbody>
          {(rep?.customers || []).map((g) => (
            <Fragment key={g.customer}>
              <tr onClick={() => setOpen(open === g.customer ? null : g.customer)} className="border-t border-white/10 cursor-pointer hover:bg-white/5" data-testid={`report-row-${g.customer}`}>
                <td className="py-2 font-bold">{g.customer}</td>
                <td className="font-mono-data text-xs text-[#A1A1AA]">{g.plates.join(", ") || "—"}</td>
                <td className="text-right">{g.trucks_loaded}</td>
                <td className="text-right font-black text-[#30D158]">{g.slabs_loaded}</td>
                <td className="text-right text-[#0A84FF]">{g.slabs_unloaded}</td>
                <td className="text-right text-[#FF9F0A]">{g.review || ""}</td>
              </tr>
              {open === g.customer && g.visits.map((v) => (
                <tr key={v.id} className="text-xs text-[#A1A1AA]">
                  <td className="pl-4 py-1">{v.day} {tm(v.arrive_ts)}–{tm(v.depart_ts)}</td>
                  <td className="font-mono-data">{v.plate || "—"}</td>
                  <td colSpan={2} className="text-right">{v.dock} · {DIR[v.direction] || v.direction} {v.slabs ? v.slabs : ""} ({v.arrive_slabs ?? "?"} → {v.depart_slabs ?? "?"})</td>
                  <td colSpan={2} className="text-right">{v.needs_review ? "NEEDS REVIEW" : v.reviewed ? "STAFF CONFIRMED" : "AI"}</td>
                </tr>
              ))}
            </Fragment>
          ))}
          {rep && !rep.customers.length && <tr><td colSpan={6} className="py-3 text-xs text-[#71717A]" data-testid="report-empty">No finished truck visits in this period.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
